/**
 * Input validation utilities
 * Prevents XSS, SQL injection, and other input-based attacks
 */

import { MESSAGE_CONSTANTS } from '@/lib/constants/appConstants';

/**
 * Validates IMEI format (15 digits)
 */
export const validateIMEI = (imei: string, isSpanish = false): { valid: boolean; error?: string } => {
  const messages = isSpanish ? MESSAGE_CONSTANTS.SPANISH.ERRORS : MESSAGE_CONSTANTS.ERRORS;

  if (!imei) {
    return { valid: false, error: messages.IMEI_REQUIRED };
  }

  const trimmed = imei.trim();
  const imeiRegex = /^\d{14,16}$/;

  if (!imeiRegex.test(trimmed)) {
    return { valid: false, error: messages.IMEI_INVALID_FORMAT };
  }

  return { valid: true };
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.EMAIL_REQUIRED };
  }

  const trimmed = email.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.EMAIL_INVALID_FORMAT };
  }

  if (trimmed.length > 254) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.EMAIL_TOO_LONG };
  }

  return { valid: true };
};

/**
 * Validates phone number format (basic US format)
 */
export const validatePhoneNumber = (phone: string): { valid: boolean; error?: string } => {
  if (!phone) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.PHONE_REQUIRED };
  }

  const trimmed = phone.trim();
  // Accept 10 digits or 11 digits (with leading 1)
  const phoneRegex = /^1?\d{10}$/;

  if (!phoneRegex.test(trimmed.replace(/[\s\-().]/g, ''))) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.PHONE_INVALID_FORMAT };
  }

  return { valid: true };
};

/**
 * Validates text input (name, etc.) - prevents XSS
 * Allows letters, spaces, hyphens, apostrophes only
 */
export const validateTextInput = (text: string): { valid: boolean; error?: string } => {
  if (!text) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.FIELD_REQUIRED };
  }

  const trimmed = text.trim();

  if (trimmed.length === 0) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.FIELD_EMPTY };
  }

  if (trimmed.length > 100) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.INPUT_TOO_LONG };
  }

  // Only allow letters, spaces, hyphens, apostrophes
  const textRegex = /^[a-zA-Z\s\-']+$/;

  if (!textRegex.test(trimmed)) {
    return { valid: false, error: MESSAGE_CONSTANTS.ERRORS.INVALID_CHARACTERS };
  }

  return { valid: true };
};

/**
 * Sanitizes string input to prevent XSS
 * Removes/escapes potentially dangerous characters
 */
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }

  return input
    .trim()
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Escapes HTML entities to prevent XSS
 */
export const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};
